import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import type { DataSourceHealth } from '../../types/dataHealth.types';

interface CrawlLogsSetupPanelProps {
  health?: DataSourceHealth;
  isLoading: boolean;
  siteId?: number;
  siteName?: string;
}

export const CrawlLogsSetupPanel: React.FC<CrawlLogsSetupPanelProps> = ({ health, isLoading, siteName }) => {
  if (isLoading) {
    return (
      <Box sx={{ px: 3 }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  const isConfigured = health?.status === 'HEALTHY';

  return (
    <Box sx={{ px: 3 }}>
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Crawl Logs Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload or configure automated crawl log ingestion to analyze Googlebot behavior and server-side SEO signals{siteName ? ` for ${siteName}` : ''}.
              </Typography>
            </Box>
            <Chip
              label={isConfigured ? 'Configured' : 'Not Configured'}
              color={isConfigured ? 'success' : 'default'}
              icon={isConfigured ? <CheckIcon /> : <ErrorIcon />}
            />
          </Box>

          <Box my={3}>
            <Typography variant="subtitle2" gutterBottom>
              What you'll get:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Googlebot crawl frequency and patterns" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Server response codes and redirect chain analysis" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Crawl budget utilization insights" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Correlation between crawls and ranking changes" />
              </ListItem>
            </List>
          </Box>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Crawl logs must be exported from your server logs (Apache, Nginx, IIS) 
              and uploaded to the SEO Engine. You can configure automated log ingestion via API or manual uploads.
            </Typography>
          </Alert>

          {health && isConfigured && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Last Upload: {health.lastSuccessfulSync ? new Date(health.lastSuccessfulSync).toLocaleString() : 'Never'}
                </Typography>
                <Typography variant="caption">
                  Data Lag: {health.lagHours ? `${health.lagHours} hours` : 'N/A'} | Coverage: {health.coveragePercent || 0}%
                </Typography>
              </Box>
            </Alert>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => alert('Crawl log upload feature coming soon!')}
            >
              {isConfigured ? 'Upload New Logs' : 'Configure Crawl Logs'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => alert('Automated ingestion setup coming soon!')}
            >
              Automated Setup
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
